/**
 * Custom: keep the "Discontinued" add-to-cart label after a variant change.
 *
 * Empire's variant handler calls `$productAtcButton.text(context.product_sold_out)`,
 * which replaces the button's entire contents (label span and spinner span) with a
 * bare "Sold out" text node. In the v12 theme this was handled by a hand-edit inside
 * an unminified empire.js; v13 ships empire.js minified, so instead of patching it we
 * watch the button and re-apply the label after the theme has written to it.
 *
 * Discontinued state comes from Liquid:
 *   - `data-product-discontinued` on the button  -> product-level metafield
 *   - `data-discontinued` on `[data-variants] option` -> per-variant metafield
 */
(function () {
  'use strict';

  var LABEL_FALLBACK = 'Discontinued';
  var SPINNER_SELECTOR = '.atc-button--icon';
  var scheduled = false;

  function label(button) {
    return button.getAttribute('data-discontinued-label') || LABEL_FALLBACK;
  }

  /**
   * A button is discontinued when the product-level flag is set, or when the
   * variant currently selected in the form's hidden variant <select> is flagged.
   */
  function isDiscontinued(button) {
    if (button.hasAttribute('data-product-discontinued')) return true;

    var form = button.closest('form');
    if (!form) return false;

    var select = form.querySelector('[data-variants]');
    if (!select) return false;

    var option = select.options[select.selectedIndex];
    return !!option && option.getAttribute('data-discontinued') === 'true';
  }

  /**
   * Stash the spinner markup while it still exists. Must run on every pass, not
   * just when we rewrite the label: empire.js destroys the spinner along with
   * everything else, so by the time we need to restore it it is already gone.
   */
  function cacheSpinner(button) {
    if (button.hasAttribute('data-spinner-html')) return;
    var spinner = button.querySelector(SPINNER_SELECTOR);
    if (spinner) button.setAttribute('data-spinner-html', spinner.outerHTML);
  }

  /**
   * Rebuild the button's inner markup. Empire's `.text()` call destroys the
   * label/spinner spans, so recreate them rather than only setting text.
   */
  function applyLabel(button) {
    var text = label(button);
    var textSpan = button.querySelector('.atc-button--text');

    // Already correct - do nothing, so we never re-trigger our own observer.
    if (textSpan && textSpan.textContent.trim() === text) return;

    var spinnerHtml = button.getAttribute('data-spinner-html');

    button.textContent = '';

    var newText = document.createElement('span');
    newText.className = 'atc-button--text';
    newText.textContent = text;
    button.appendChild(newText);

    if (spinnerHtml) button.insertAdjacentHTML('beforeend', spinnerHtml);
  }

  function sync() {
    scheduled = false;

    var buttons = document.querySelectorAll('[data-product-atc]');
    for (var i = 0; i < buttons.length; i++) {
      cacheSpinner(buttons[i]);
      // Only override the theme once it has decided the variant is unavailable;
      // an available variant must keep its working "Add to cart" label.
      if (buttons[i].disabled && isDiscontinued(buttons[i])) applyLabel(buttons[i]);
    }
  }

  // setTimeout rather than requestAnimationFrame: rAF is paused in background
  // tabs, which would leave the label as "Sold out" until the tab is focused.
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    window.setTimeout(sync, 0);
  }

  function init() {
    sync();

    // childList/characterData only: empire.js swaps the button's children, and
    // ignoring attributes keeps our own data-spinner-html write from looping.
    new MutationObserver(schedule).observe(document.documentElement, {
      childList: true,
      characterData: true,
      subtree: true
    });

    // Covers quick-shop modals and any other dynamically injected product form.
    document.addEventListener('change', function (event) {
      if (event.target.closest && event.target.closest('form')) schedule();
    }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
